import GridSpinner from 'components/UI/GridSpinner'
import formStyles from 'modules/Forms/common-form.module.scss'
import PaymentProfileCreateForm from 'modules/Forms/Finance/PaymentProfileCreate'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouteMatch } from 'react-router'
import { useHistory } from 'react-router-dom'
import useQueryParams from 'utils/hooks/useQueryParams'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import validator from 'utils/asyncValidator'
import validationSchema from 'modules/Forms/Finance/PaymentProfileCreate/validationSchema'
import BILLING_API_V1 from 'api/billing/v1'
import { getFormValues } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
import union from 'lodash/union'
import { clearFormSchema, setFormSchema } from 'modules/Forms/FormMeta/actions'
import extractRequiredFields from 'utils/extractRequiredFields'
import PageHeader from 'components/PageHeader'
import clsx from 'clsx'
import { usePaymentProfiles } from 'modules/Finance/PaymentProfiles/hooks/usePaymentProfiles'
import uniqBy from 'lodash/uniqBy'

const FORM_NAME = 'PaymentProfileCreate'

const Create = () => {
  const history = useHistory()
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const selector = getFormValues(FORM_NAME)
  const { i18n } = useSelector(({ app }) => app)

  const {
    params: { profileId },
  } = useRouteMatch()
  const { t } = useTranslation()
  const { query: { goBack } = {} } = useQueryParams()
  const {
    query: { payment_method },
  } = useQueryParams()

  const { paymentProfile: { data: paymentProfile = {}, loading, loaded } = {} } = useSelector(
    (state) => state['finance/payment-profiles'],
  )

  const { getPaymentProfileById, resetPaymentProfiles } = usePaymentProfiles()

  const { formValues } = useSelector((state) => ({
    formValues: selector(state),
  }))

  useEffect(() => {
    if (profileId) {
      getPaymentProfileById({ profileId }).catch(() =>
        history.push(goBack ? goBack : pathGen(ROUTES.FINANCE_PAYMENT_PROFILES)),
      )
    }
    return () => {
      resetPaymentProfiles()
    }
  }, [])

  const profileTitle = useMemo(
    () =>
      paymentProfile?.details?.organisation_name ||
      paymentProfile?.details?.lastname + ' ' + paymentProfile?.details?.firstname ||
      '',
    [paymentProfile],
  )

  const [availableMethods, setAvailableMethods] = useState([])
  const [loadingSettings, setLoadingSettings] = useState(false)

  useEffect(() => {
    setLoadingSettings(true)
    BILLING_API_V1.fetchPayerSettings({
      params: { payment_method },
    })
      .then(({ response }) => {
        if (!response?.data) {
          return setLoadingSettings(false)
        }

        setAvailableMethods(
          response.data.filter((method) =>
            payment_method ? method.payment_method === payment_method : method,
          ),
        )
      })
      .finally(() => {
        setLoadingSettings(false)
      })
  }, [i18n])

  const legalTypes = useMemo(
    () => availableMethods.reduce((acc, item) => union(acc, [item.legal_type]), []),
    [availableMethods, payment_method],
  )

  const schema = useMemo(
    () => validationSchema({ legalTypes, formValues }),
    [legalTypes, formValues],
  )

  const legalTypesCountryCodes = useMemo(() => {
    return legalTypes.reduce((acc, legaltype) => {
      acc[legaltype] = availableMethods
        .filter((am) => am.legal_type === legaltype)
        .reduce((acc, method) => {
          acc = uniqBy(acc.concat(method.countries), 'id')
          return acc
        }, [])

      return acc
    }, {})
  }, [legalTypes, availableMethods])

  useEffect(() => {
    dispatch(setFormSchema(FORM_NAME, extractRequiredFields(schema)))
  }, [schema])

  useEffect(() => {
    return () => dispatch(clearFormSchema(FORM_NAME))
  }, [])

  return (
    <div className={clsx(formStyles['formContainer'], 'layout-tile')}>
      <div className={formStyles['formContainer__inner']}>
        {profileId && (!loaded || loading) ? (
          <GridSpinner />
        ) : (
          <>
            <PageHeader className={'margin-bottom-40'}>
              {t('payment_profiles::new.header', {
                context: payment_method ? 'for_method' : profileId ? 'edit' : 'new',
                title: profileTitle,
                payment_method: `"${t(`billing::methods.${payment_method}`)}"`,
              })}
            </PageHeader>

            <PaymentProfileCreateForm
              loadingSettings={loadingSettings}
              profileId={profileId}
              initialValues={paymentProfile}
              onClose={() =>
                history.push(goBack ? goBack : pathGen(ROUTES.FINANCE_PAYMENT_PROFILES))
              }
              legalTypes={legalTypes}
              legalTypesCountryCodes={legalTypesCountryCodes}
              asyncValidate={validator(schema)}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default Create
